/* global window */
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { call, put, takeLatest, all } from 'redux-saga/effects';

import { addAPIError, removeAPIError } from 'redux/apiErrors/sagas';
import { GET_CART_REQUEST } from 'redux/cart/constants';
import { GET_CLUB_SIGNUP_REQUEST } from 'redux/clubSignup/constants';
import { getDefault as getDefaultAddress } from 'redux/customerAddresses/sagas';
import { GET_RESERVATION_CART_REQUEST } from 'redux/reservationCart/constants';

import {
  getCustomer,
  loginCustomer,
  fbLoginOrCreateCustomer,
  googleLoginOrCreateCustomer,
  addCustomer,
  addCustomerWithAddress,
  editCustomer,
  editCustomerPassword,
  forgotPassword,
  resetPassword,
  validateResetPasswordId,
  magicLink,
  validateMagicLinkId,
  logoutCustomer,
  unsubscribeCustomer
} from './api';
import {
  GET_CUSTOMER_REQUEST,
  GET_CUSTOMER_WITH_DEFAULTS_REQUEST,
  GET_CUSTOMER_REQUESTING,
  GET_CUSTOMER_SUCCESS,
  ADD_CUSTOMER_REQUEST,
  ADD_CUSTOMER_WITH_ADDRESS_REQUEST,
  EDIT_CUSTOMER_REQUEST,
  EDIT_PASSWORD_REQUEST,
  LOGIN_REQUEST,
  FB_LOGIN_OR_CREATE_REQUEST,
  GOOGLE_LOGIN_OR_CREATE_REQUEST,
  LOGIN_REQUESTING,
  LOGOUT_REQUEST,
  LOGOUT_REQUESTING,
  LOGOUT_SUCCESS,
  FORGOT_PASSWORD_REQUEST,
  FORGOT_PASSWORD_REQUESTING,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_REQUESTING,
  RESET_PASSWORD_ID_REQUEST,
  MAGIC_LINK_REQUEST,
  MAGIC_LINK_REQUESTING,
  MAGIC_LINK_ID_REQUEST,
  UNSUBSCRIBE_CUSTOMER_REQUEST
} from './constants';

export function* get() {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_REQUESTING });
    if (!customerId()) {
      yield put({ type: GET_CUSTOMER_SUCCESS, payload: null });
    } else {
      const response = yield call(getCustomer);
      yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      Cookies.remove('customerToken');
      yield put({ type: GET_CUSTOMER_SUCCESS, payload: null });
    }
    yield call(addAPIError, error);
  }
}

export function* getWithDefault() {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_REQUESTING });
    const response = yield call(getCustomer);
    yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    yield call(getDefaultAddress);
  } catch (error) {
    if (error.response && error.response.status === 404) {
      Cookies.remove('customerToken');
      yield put({ type: GET_CUSTOMER_SUCCESS, payload: null });
    }
    yield call(addAPIError, error);
  }
}

export function* add(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_REQUESTING });
    const { customer } = action;
    customer.orderInformation = {
      acquisitionChannel: 'Web'
    };
    const response = yield call(addCustomer, customer);
    yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    const newAction = {
      email: action.customer.emails[0].email,
      password: action.customer.password
    };
    yield call(login, newAction);
    if (action.successFunction) {
      action.successFunction(response.data);
    }
    return response.data;
  } catch (error) {
    yield call(addAPIError, error);
    return null;
  }
}

export function* addWithAddress(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_REQUESTING });
    const { customer } = action;
    customer.orderInformation = {
      acquisitionChannel: 'Web'
    };
    const response = yield call(addCustomerWithAddress, customer);
    yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    const newAction = {
      email: action.customer.emails[0].email,
      password: action.customer.password
    };
    yield call(login, newAction);
    if (action.successFunction) {
      action.successFunction(response.data);
    }
    return response.data;
  } catch (error) {
    yield call(addAPIError, error);
    return null;
  }
}

export function* edit(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_REQUESTING });
    const response = yield call(editCustomer, customerId(), action.customer);
    yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch (error) {
    yield call(addAPIError, error);
  }
}

export function* editPassword(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_REQUESTING });
    const response = yield call(
      editCustomerPassword,
      customerId(),
      action.customer
    );
    yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch (error) {
    yield call(addAPIError, error);
  }
}

export function* login(action) {
  const loginObject = {
    email: action.email.toLowerCase(),
    password: action.password
  };
  yield call(consolidatedLogin, action, loginObject, 'basic');
}

export function* fbLoginOrCreate(action) {
  const loginObject = {
    fbAccessToken: action.accessToken
  };
  yield call(consolidatedLogin, action, loginObject, 'facebook');
}

export function* googleLoginOrCreate(action) {
  const loginObject = {
    googleTokenId: action.tokenId
  };
  yield call(consolidatedLogin, action, loginObject, 'google');
}

export function* magicLinkIdValid(action) {
  const loginObject = {
    magicLinkId: action.magicLinkId
  };
  yield call(consolidatedLogin, action, loginObject, 'magicLink');
}

export function* resetPasswordIdValid(action) {
  const loginObject = {
    resetPasswordId: action.resetPasswordId
  };
  yield call(consolidatedLogin, action, loginObject, 'resetPassword');
}

export function* consolidatedLogin(action, loginObj, type) {
  try {
    yield call(removeAPIError);
    yield put({ type: LOGIN_REQUESTING });
    const loginObject = loginObj;
    if (cartId()) {
      loginObject.cartId = cartId();
    }
    if (clubSignupId()) {
      loginObject.clubSignupId = clubSignupId();
    }
    if (reservationCartId()) {
      loginObject.reservationCartId = reservationCartId();
    }
    let response;
    switch (type) {
      case 'facebook': {
        response = yield call(fbLoginOrCreateCustomer, loginObject);
        break;
      }
      case 'google': {
        response = yield call(googleLoginOrCreateCustomer, loginObject);
        break;
      }
      case 'magicLink': {
        response = yield call(validateMagicLinkId, loginObject);
        break;
      }
      case 'resetPassword': {
        response = yield call(validateResetPasswordId, loginObject);
        break;
      }
      case 'basic': {
        response = yield call(loginCustomer, loginObject);
        break;
      }
      default: {
        throw new Error('Invalid Login Type');
      }
    }
    Cookies.set('customerToken', response.data.token, {
      expires: 365,
      secure: process.env.NODE_ENV === 'production'
    });
    yield put({ type: GET_CUSTOMER_REQUEST });
    if (cartId()) {
      yield put({ type: GET_CART_REQUEST });
    }
    if (clubSignupId()) {
      yield put({ type: GET_CLUB_SIGNUP_REQUEST });
    }
    if (reservationCartId()) {
      yield put({ type: GET_RESERVATION_CART_REQUEST });
    }
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch (error) {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 404)
    ) {
      action.errorFunction();
    } else {
      yield call(addAPIError, error);
    }
  }
}

export function* forgot(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: FORGOT_PASSWORD_REQUESTING });
    const forgotObject = {
      email: action.data.email.toLowerCase(),
      redirectLink: action.data.redirectLink
    };
    yield call(forgotPassword, forgotObject);
    if (action.successFunction) {
      action.successFunction();
    }
  } catch (error) {
    if (
      error.response &&
      error.response.status === 404 &&
      action.errorFunction
    ) {
      action.errorFunction();
    } else {
      yield call(addAPIError, error);
    }
  }
}

export function* magic(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: MAGIC_LINK_REQUESTING });
    const magicObject = {
      email: action.data.email.toLowerCase(),
      redirectLink: action.data.redirectLink
    };
    yield call(magicLink, magicObject);
    if (action.successFunction) {
      action.successFunction();
    }
  } catch (error) {
    if (
      error.response &&
      error.response.status === 404 &&
      action.errorFunction
    ) {
      action.errorFunction();
    } else {
      yield call(addAPIError, error);
    }
  }
}

export function* reset(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: RESET_PASSWORD_REQUESTING });
    const response = yield call(resetPassword, action.resetObj);
    Cookies.set('customerToken', response.data.token, {
      expires: 365,
      secure: process.env.NODE_ENV === 'production'
    });
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch (error) {
    yield call(addAPIError, error);
  }
}

export function* unsubscribe(action) {
  try {
    yield call(removeAPIError);
    const response = yield call(
      unsubscribeCustomer,
      action.customerId,
      action.payload
    );
    // yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    action.successFunction(response.data);
  } catch (error) {
    yield call(addAPIError, error);
  }
}

export function* logout() {
  yield call(removeAPIError);
  yield put({ type: LOGOUT_REQUESTING });
  try {
    yield call(logoutCustomer);
  } catch {
    // Fail silenetly
  }
  Cookies.remove(`customerToken`);
  Cookies.remove(`cartId`);
  Cookies.remove(`clubSignupId`);
  Cookies.remove(`reservationCartId`);
  yield put({ type: LOGOUT_SUCCESS });
  yield put({ type: GET_CART_REQUEST });
  window.location.href = '/profile/logout';
}

export function* watchList() {
  yield takeLatest(ADD_CUSTOMER_REQUEST, add);
  yield takeLatest(ADD_CUSTOMER_WITH_ADDRESS_REQUEST, addWithAddress);
  yield takeLatest(EDIT_CUSTOMER_REQUEST, edit);
  yield takeLatest(EDIT_PASSWORD_REQUEST, editPassword);
  yield takeLatest(GET_CUSTOMER_REQUEST, get);
  yield takeLatest(GET_CUSTOMER_WITH_DEFAULTS_REQUEST, getWithDefault);
  yield takeLatest(LOGIN_REQUEST, login);
  yield takeLatest(FB_LOGIN_OR_CREATE_REQUEST, fbLoginOrCreate);
  yield takeLatest(GOOGLE_LOGIN_OR_CREATE_REQUEST, googleLoginOrCreate);
  yield takeLatest(FORGOT_PASSWORD_REQUEST, forgot);
  yield takeLatest(RESET_PASSWORD_ID_REQUEST, resetPasswordIdValid);
  yield takeLatest(RESET_PASSWORD_REQUEST, reset);
  yield takeLatest(MAGIC_LINK_REQUEST, magic);
  yield takeLatest(MAGIC_LINK_ID_REQUEST, magicLinkIdValid);
  yield takeLatest(LOGOUT_REQUEST, logout);
  yield takeLatest(UNSUBSCRIBE_CUSTOMER_REQUEST, unsubscribe);
}

export default function* customerSagas() {
  yield all([watchList()]);
}

const customerId = () => {
  if (!Cookies.get('customerToken')) {
    return undefined;
  }
  return jwtDecode(Cookies.get('customerToken')).sub;
};
const cartId = () => Cookies.get('cartId');
const clubSignupId = () => Cookies.get('clubSignupId');
const reservationCartId = () => Cookies.get('reservationCartId');
